import { type MouseEventHandler, type FC, useState, useEffect, useRef } from 'react';
import type { NavigationItem } from '../navigationTypes';
import type { ResponsiveFlyoutHeaderProps } from './ResponsiveFlyoutHeader';
import { ResponsiveFlyoutHeader } from './ResponsiveFlyoutHeader';
import { getNavigationItemKey } from '../getNavigationItemKey';
import { ResponsiveFlyoutTransition } from './ResponsiveFlyoutTransition';
import { ResponsiveFlyoutContent } from './ResponsiveFlyoutContent';
import type { TreeNavigationDirection } from './useTreeNavigationPopup';

export type ResponsiveFlyoutProps = {
  item?: NavigationItem;
  direction: TreeNavigationDirection;
  onItemClick?: (item: NavigationItem) => void;
  onNavigate?: (item: NavigationItem) => void;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
} & Pick<ResponsiveFlyoutHeaderProps, 'isVisible' | 'onClose' | 'onBack'>;

/**
 * Navigation popup shown to the user when they interact with the main navigation, displaying child navigation items in the nav tree.
 */

export const ResponsiveFlyout: FC<ResponsiveFlyoutProps> = ({
  item,
  direction,
  onClose,
  onBack,
  onItemClick,
  onNavigate,
  onMouseLeave,
  isVisible,
}) => {
  const [newItem, setItem] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    newItem && buttonRef.current?.focus();
    setItem(false);
  }, [newItem]);

  const transitionKey = item && getNavigationItemKey(item);

  return (
    <div
      className="bg-color-background-base lg:bg-color-grey-light flex w-full flex-1 flex-col pb-4"
      onMouseLeave={onMouseLeave}
    >
      <div className="flex flex-1 flex-col gap-2">
        <ResponsiveFlyoutHeader
          isVisible={isVisible}
          ref={buttonRef}
          title={item?.displayName}
          onBack={onBack}
          onClose={onClose}
        />
        <ResponsiveFlyoutTransition direction={direction} transitionKey={transitionKey}>
          {(ref) => (
            <ResponsiveFlyoutContent
              ref={ref}
              item={item}
              onItem={setItem}
              onItemClick={onItemClick}
              onNavigate={onNavigate}
            />
          )}
        </ResponsiveFlyoutTransition>
      </div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default ResponsiveFlyout;
